import React, { useState, useEffect } from 'react';
import LessonLayout from '../components/LessonLayout';
import { Card, Row, Col, Alert, Button, Badge, Form } from 'react-bootstrap';

const Lesson05_ConditionalRender = () => {
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [userRole, setUserRole] = useState('guest');
  const [showMessage, setShowMessage] = useState(true);
  const [score, setScore] = useState(75);
  const [theme, setTheme] = useState('light');

  useEffect(() => {
    console.log('📖 Lesson 05: Conditional Rendering - Loaded');
    console.log('📚 Different patterns for conditional rendering in React');
  }, []);

  // Pattern 1: If-Else Statement (using function)
  const renderWithIfElse = () => {
    console.log('🔀 Pattern 1: If-Else - isLoggedIn:', isLoggedIn);
    
    if (isLoggedIn) {
      return (
        <Alert variant="success">
          <strong>✅ Welcome back!</strong> You are logged in.
        </Alert>
      );
    } else {
      return (
        <Alert variant="warning">
          <strong>⚠️ Please log in</strong> to access your account.
        </Alert>
      );
    }
  };

  // Pattern 2: Ternary Operator
  const renderWithTernary = () => {
    console.log('🔀 Pattern 2: Ternary Operator - showMessage:', showMessage);
    
    return (
      <div>
        {showMessage ? (
          <Alert variant="info">
            <strong>📢 Message is visible</strong> (using ternary operator)
          </Alert>
        ) : (
          <Alert variant="secondary">
            <strong>🔇 Message is hidden</strong> (using ternary operator)
          </Alert>
        )}
      </div>
    );
  };

  // Pattern 3: Logical && Operator
  const renderWithAnd = () => {
    console.log('🔀 Pattern 3: Logical && - isLoggedIn:', isLoggedIn);
    
    return (
      <div>
        {isLoggedIn && (
          <Alert variant="primary">
            <strong>👤 User Profile</strong> is now visible (using &&)
          </Alert>
        )}
        {!isLoggedIn && (
          <Alert variant="light">
            <strong>🔒 Login required</strong> to view profile (using &&)
          </Alert>
        )}
      </div>
    );
  };

  // Pattern 4: Switch Statement
  const renderWithSwitch = (role) => {
    console.log('🔀 Pattern 4: Switch Statement - userRole:', role);
    
    switch (role) {
      case 'admin':
        return (
          <Alert variant="danger">
            <strong>👑 Admin Panel</strong> - Full access granted
          </Alert>
        );
      case 'editor':
        return (
          <Alert variant="warning">
            <strong>✏️ Editor Panel</strong> - Edit access granted
          </Alert>
        );
      case 'viewer':
        return (
          <Alert variant="info">
            <strong>👁️ Viewer Panel</strong> - Read-only access
          </Alert>
        );
      case 'guest':
      default:
        return (
          <Alert variant="secondary">
            <strong>🚪 Guest Access</strong> - Limited features available
          </Alert>
        );
    }
  };

  // Pattern 5: Null/Nothing Rendering
  const renderWithNull = () => {
    console.log('🔀 Pattern 5: Null Rendering - score:', score);
    
    // Don't render anything if score is below 50
    if (score < 50) {
      console.log('   → Rendering nothing (returning null)');
      return null;
    }
    
    console.log('   → Rendering component');
    return (
      <Alert variant="success">
        <strong>🎉 Congratulations!</strong> Your score {score} is passing!
      </Alert>
    );
  };

  // Pattern 6: Multiple Conditions (Nested Ternary)
  const getGradeColor = () => {
    return score >= 90 ? 'success' : 
           score >= 75 ? 'primary' : 
           score >= 60 ? 'warning' : 
           'danger';
  };

  const getGradeLabel = () => {
    return score >= 90 ? 'Excellent' : 
           score >= 75 ? 'Good' : 
           score >= 60 ? 'Fair' : 
           'Needs Improvement';
  };

  const handleLogin = () => {
    console.log('🔑 Login button clicked');
    setIsLoggedIn(!isLoggedIn);
    console.log('   New login status:', !isLoggedIn);
  };

  const handleToggleMessage = () => {
    console.log('💬 Toggle message clicked');
    setShowMessage(!showMessage);
    console.log('   Show message:', !showMessage);
  };

  const handleRoleChange = (e) => {
    const newRole = e.target.value;
    console.log('👤 Role changed to:', newRole);
    setUserRole(newRole);
  };

  const handleScoreChange = (e) => {
    const newScore = parseInt(e.target.value);
    console.log('📊 Score changed to:', newScore);
    setScore(newScore);
  };

  const handleThemeChange = () => {
    const newTheme = theme === 'light' ? 'dark' : 'light';
    console.log('🎨 Theme changed from', theme, 'to', newTheme);
    setTheme(newTheme);
  };

  return (
    <LessonLayout 
      lessonNumber={5}
      title="Conditional Rendering" 
      description="Master different patterns for conditional rendering in React"
    >
      <Alert variant="success">
        <strong>What You'll Learn:</strong>
        <ul className="mb-0 mt-2">
          <li>If-Else statements for conditional rendering</li>
          <li>Ternary operators for inline conditions</li>
          <li>Logical && operator for simple conditions</li>
          <li>Switch statements for multiple conditions</li>
          <li>Rendering null to hide components</li>
        </ul>
      </Alert>

      {/* Pattern 1: If-Else */}
      <h4 className="mt-4">Pattern 1: If-Else Statement</h4>
      <Card className="mb-3">
        <Card.Header>
          <div className="d-flex justify-content-between align-items-center">
            <span>If-Else Rendering</span>
            <Button 
              variant={isLoggedIn ? 'danger' : 'success'} 
              size="sm"
              onClick={handleLogin}
            >
              {isLoggedIn ? 'Logout' : 'Login'}
            </Button>
          </div>
        </Card.Header>
        <Card.Body>
          {renderWithIfElse()}
          <pre className="code-example mt-3">
{`if (isLoggedIn) {
  return <WelcomeUser />;
} else {
  return <LoginPrompt />;
}`}
          </pre>
        </Card.Body>
      </Card>

      {/* Pattern 2: Ternary Operator */}
      <h4 className="mt-4">Pattern 2: Ternary Operator</h4>
      <Card className="mb-3">
        <Card.Header>
          <div className="d-flex justify-content-between align-items-center">
            <span>Ternary Operator (Inline)</span>
            <Button variant="info" size="sm" onClick={handleToggleMessage}>
              Toggle Message
            </Button>
          </div>
        </Card.Header>
        <Card.Body>
          {renderWithTernary()}
          <pre className="code-example mt-3">
{`{showMessage ? (
  <Message />
) : (
  <HiddenMessage />
)}`}
          </pre>
        </Card.Body>
      </Card>

      {/* Pattern 3: Logical && */}
      <h4 className="mt-4">Pattern 3: Logical && Operator</h4>
      <Card className="mb-3">
        <Card.Header>Best for showing/hiding single element</Card.Header>
        <Card.Body>
          {renderWithAnd()}
          <pre className="code-example mt-3">
{`{isLoggedIn && <UserProfile />}
{!isLoggedIn && <LoginButton />}`}
          </pre>
        </Card.Body>
      </Card>

      {/* Pattern 4: Switch Statement */}
      <h4 className="mt-4">Pattern 4: Switch Statement</h4>
      <Card className="mb-3">
        <Card.Header>
          <div className="d-flex justify-content-between align-items-center">
            <span>Switch for Multiple Conditions</span>
            <Form.Select 
              size="sm" 
              style={{ width: '150px' }}
              value={userRole}
              onChange={handleRoleChange}
            >
              <option value="guest">Guest</option>
              <option value="viewer">Viewer</option>
              <option value="editor">Editor</option>
              <option value="admin">Admin</option>
            </Form.Select>
          </div>
        </Card.Header>
        <Card.Body>
          {renderWithSwitch(userRole)}
          <pre className="code-example mt-3">
{`switch (userRole) {
  case 'admin':
    return <AdminPanel />;
  case 'editor':
    return <EditorPanel />;
  default:
    return <GuestView />;
}`}
          </pre>
        </Card.Body>
      </Card>

      {/* Pattern 5: Null Rendering */}
      <h4 className="mt-4">Pattern 5: Render Nothing (null)</h4>
      <Card className="mb-3">
        <Card.Header>
          <div className="d-flex justify-content-between align-items-center">
            <span>Hide Component by Returning null</span>
            <div>
              Score: <Badge bg={getGradeColor()}>{score}</Badge>
            </div>
          </div>
        </Card.Header>
        <Card.Body>
          <Form.Group className="mb-3">
            <Form.Label>Adjust Score: {score}</Form.Label>
            <Form.Range 
              min="0" 
              max="100" 
              value={score}
              onChange={handleScoreChange}
            />
            <small className="text-muted">
              Component only renders if score ≥ 50
            </small>
          </Form.Group>
          {renderWithNull()}
          <pre className="code-example">
{`if (score < 50) {
  return null; // Don't render anything
}
return <PassingMessage />;`}
          </pre>
        </Card.Body>
      </Card>

      {/* Pattern 6: Complex Conditions */}
      <h4 className="mt-4">Pattern 6: Multiple Nested Conditions</h4>
      <Card className="mb-3">
        <Card.Header>Nested Ternary for Multiple Outcomes</Card.Header>
        <Card.Body>
          <Alert variant={getGradeColor()}>
            <strong>Grade: {getGradeLabel()}</strong> (Score: {score})
          </Alert>
          <pre className="code-example">
{`const grade = 
  score >= 90 ? 'Excellent' :
  score >= 75 ? 'Good' :
  score >= 60 ? 'Fair' :
  'Needs Improvement';`}
          </pre>
        </Card.Body>
      </Card>

      {/* Pattern 7: Element Variables */}
      <h4 className="mt-4">Pattern 7: Element Variables</h4>
      <Card className="mb-3">
        <Card.Header>
          <div className="d-flex justify-content-between align-items-center">
            <span>Store JSX in Variables</span>
            <Button variant="secondary" size="sm" onClick={handleThemeChange}>
              Theme: {theme}
            </Button>
          </div>
        </Card.Header>
        <Card.Body>
          {(() => {
            let content;
            if (theme === 'light') {
              content = <Alert variant="light">☀️ Light theme active</Alert>;
            } else {
              content = <Alert variant="dark" className="text-white">🌙 Dark theme active</Alert>;
            }
            console.log('🎨 Pattern 7: Element Variable - theme:', theme);
            return content;
          })()}
          <pre className="code-example">
{`let content;
if (theme === 'light') {
  content = <LightTheme />;
} else {
  content = <DarkTheme />;
}
return <div>{content}</div>;`}
          </pre>
        </Card.Body>
      </Card>

      {/* Comparison Table */}
      <h4 className="mt-4">📊 Conditional Rendering Patterns Comparison</h4>
      <Card>
        <Card.Body>
          <table className="table comparison-table">
            <thead>
              <tr>
                <th>Pattern</th>
                <th>Best For</th>
                <th>Readability</th>
                <th>Example</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>If-Else</td>
                <td>Complex logic, multiple lines</td>
                <td>⭐⭐⭐⭐⭐</td>
                <td><code>if (x) return &lt;A/&gt; else return &lt;B/&gt;</code></td>
              </tr>
              <tr>
                <td>Ternary</td>
                <td>Inline, two options</td>
                <td>⭐⭐⭐⭐</td>
                <td><code>{`{x ? <A/> : <B/>}`}</code></td>
              </tr>
              <tr>
                <td>Logical &&</td>
                <td>Show/hide single element</td>
                <td>⭐⭐⭐⭐⭐</td>
                <td><code>{`{x && <A/>}`}</code></td>
              </tr>
              <tr>
                <td>Switch</td>
                <td>Many mutually exclusive options</td>
                <td>⭐⭐⭐⭐</td>
                <td><code>switch(x) case 'a': return &lt;A/&gt;</code></td>
              </tr>
              <tr>
                <td>Return null</td>
                <td>Hide entire component</td>
                <td>⭐⭐⭐⭐⭐</td>
                <td><code>if (!show) return null</code></td>
              </tr>
            </tbody>
          </table>
        </Card.Body>
      </Card>

      <Alert variant="warning" className="mt-4">
        <strong>💡 Best Practices:</strong>
        <ul className="mb-0">
          <li>Use && for simple show/hide (but watch out for 0 or '' which render)</li>
          <li>Use ternary for two clear alternatives</li>
          <li>Use if-else for complex conditions with multiple statements</li>
          <li>Use switch for 3+ mutually exclusive options</li>
          <li>Return null early to avoid rendering entire component</li>
          <li>Avoid deeply nested ternaries - they're hard to read</li>
        </ul>
      </Alert>
    </LessonLayout>
  );
};

export default Lesson05_ConditionalRender;
